import * as Sentry from "@sentry/react";
import Cookies from "universal-cookie";
import { UserWithBillingPlan } from "../components/context/AppContext";
import { showErrorToast, showInfoToast, showSuccessToast } from "../components/shared/Toasts";
import { SEC_IN_YEAR, SEC_IN_DAY, FieldMappingSource } from "../constants";
import {
  LinkedAccount,
  User,
  CommonModelToggle,
  CateogryScopeMap,
  CommonModelField,
  CommonModelFieldMap,
} from "../models/Entities";
import { LOGIN_PATH } from "../router/RouterUtils";
import { createFieldMappingOptions } from "../components/pages/configuration/field-mappings/utils/FieldMappingUtils";
import {
  IntegrationCustomization,
  OrganizationCustomization,
} from "../components/pages/configuration/link/context/CustomizationContext";
import { HTTPMethod, clearSwitchedFrom } from "@merge-api/merge-javascript-shared";
import { WhiteLabelGuideType } from "../components/pages/configuration/link/context/CustomizationContext";
import { clearPostHogIdentity } from "../posthog/identify";

export interface UserSuccessData {
  token: string;
  user: User;
  created: boolean;
  organization: any;
}

export interface MagicLinkResponse {
  magic_link_url: string;
}
export interface FormErrorData {
  [key: string]: string[];
}

export interface ConflictErrorData {
  [key: string]: string;
}

export interface MultipleFormErrorData {
  [index: number]: FormErrorData;
}

export interface PaginatedAPIResponse<T> {
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface SSOLoginData {
  sso_url: string;
}

export enum Tenancy {
  SingleTenant,
  NAMultiTenant,
  EUMultiTenant,
  APMultiTenant,
  Local,
}

export const VALID_TENANT_URLS = [
  "https://api.merge.dev",
  "https://api-eu.merge.dev",
  "https://api-ap.merge.dev",
  "https://api-sandbox.merge.dev",
  "https://api-oyster-eu.merge.dev",
  "https://api-payfit.merge.dev",
  "https://api-develop.merge.dev",
  "https://api-oai-usw2.openaimerge.com",
  "https://api-oai-euw1.openaimerge.com",
  "https://api-oai-apne2.openaimerge.com",
  "https://api-oai-apse2.openaimerge.com",
  "https://api-amex-use1.merge.dev",
  "https://api-uber-usw2.merge.dev",
  "https://api-usw2.dropboxmerge.com",
  "https://api-mu-develop.merge.dev",
  "https://api-mu-staging.merge.dev",
  "http://localhost:8000",
];

export const TENANT_URL_TO_FRONTEND_URL: Record<string, string> = {
  "https://api-develop.merge.dev": "https://develop.merge.dev",
  "http://localhost:8000": "http://localhost:3000",
  "https://api-usw2.dropboxmerge.com": "https://app-usw2.dropboxmerge.com",
  "https://api-mu-develop.merge.dev": "https://app-mu-develop.merge.dev",
  "https://api-mu-staging.merge.dev": "https://app-mu-staging.merge.dev",
};

const getApiHostname = (): string => {
  const apiUrl = process.env.REACT_APP_BASE_API_URL;
  if (apiUrl) {
    try {
      return new URL(apiUrl).hostname;
    } catch {
      // fall through to switch
    }
  }
  switch (process.env.REACT_APP_MERGE_ENV) {
    case "PRODUCTION":
      return "api.merge.dev";
    case "SANDBOX":
      return "api-sandbox.merge.dev";
    case "DEVELOP":
      return "api-develop.merge.dev";
    case "MU-DEVELOP":
      return "api-mu-develop.merge.dev";
    case "MU-STAGING":
      return "api-mu-staging.merge.dev";
    default:
      return "localhost";
  }
};

export const AUTH_COOKIE_SUFFIX = getApiHostname();
const AUTH_TOKEN_INDICATOR = "authentication_token_indicator";
const AUTH_COOKIE_NAME = `authentication_token_${AUTH_COOKIE_SUFFIX}`;
const AUTH_INDICATOR_NAME = `authentication_token_indicator_${AUTH_COOKIE_SUFFIX}`;
const USER_TYPE_COOKIE_NAME = `user_type_${AUTH_COOKIE_SUFFIX}`;
export const CSRF_COOKIE_NAME = `merge_csrftoken_${AUTH_COOKIE_SUFFIX}`;

/**
 * This generic `Result` type enables an API promise to return either a result or an error with one return
 * value. Use it everywhere possible with a `Promise` wrapped around it so our API client calls can become:
 * ```
 * const result = await fetchDataFromAPI(inputInfo)
 * if (result.status === 'success') {
 *     ...
 * } else {
 *     ...
 * }
 * ```
 */
export type Result<ResultType, ErrorType = Response | undefined> =
  | {
      status: "success";
      data: ResultType;
    }
  | { status: "error"; error: ErrorType };

export const API_DOMAIN = (() => {
  switch (process.env.REACT_APP_MERGE_ENV) {
    case "PRODUCTION":
      const base_url = process.env.REACT_APP_BASE_API_URL;
      if (!base_url) {
        Sentry.captureMessage("Base API URL not found for production dashboard.");
      }
      return base_url;
    case "SANDBOX":
      return "https://api-sandbox.merge.dev";
    case "DEVELOP":
      return "https://api-develop.merge.dev";
    case "MU-DEVELOP":
      return "https://api-mu-develop.merge.dev";
    case "MU-STAGING":
      return "https://api-mu-staging.merge.dev";
    case "LOCAL":
    default:
      return "http://localhost:8000";
  }
})();

export const getTenancy = (): Tenancy => {
  const mergeEnv = process.env.REACT_APP_MERGE_ENV;
  if (!mergeEnv || mergeEnv == "LOCAL") {
    return Tenancy.Local;
  }

  const baseAPIURL = process.env.REACT_APP_BASE_API_URL;
  if (baseAPIURL == "https://api.merge.dev") {
    return Tenancy.NAMultiTenant;
  }

  if (baseAPIURL == "https://api-eu.merge.dev") {
    return Tenancy.EUMultiTenant;
  }

  if (baseAPIURL == "https://api-ap.merge.dev") {
    return Tenancy.APMultiTenant;
  }

  return Tenancy.SingleTenant;
};

export const apiURLForPath = (path: string): string => {
  const baseURL = `${API_DOMAIN}/api`;
  if (path.charAt(0) != "/") {
    return `${baseURL}/${path}`;
  }
  return baseURL + path;
};

export const apiURLForPathWithOverrideUrl = (url: string, path: string): string => {
  const baseURL = `${url}/api`;
  if (path.charAt(0) != "/") {
    return `${baseURL}/${path}`;
  }
  return baseURL + path;
};

export const fetchWithoutAuth = ({
  path,
  method = HTTPMethod.GET,
  headers = {},
  body,
  onResponse,
  onError,
}: {
  path: string;
  method?: string;
  headers?: { [key: string]: string };
  body?: any;
  onResponse: (response: any) => void;
  onError?: (response: Response | undefined) => void;
}): Promise<void> => {
  const url = apiURLForPath(path);
  return fetchWithoutAuthHelper({
    url,
    method,
    headers,
    body,
    onResponse,
    onError,
  });
};

export const fetchWithoutAuthWithBaseAPIUrlOverride = ({
  path,
  method = HTTPMethod.GET,
  headers = {},
  body,
  overrideBaseAPIUrl,
  onResponse,
  onError,
}: {
  path: string;
  method: string;
  headers?: { [key: string]: string };
  body?: any;
  overrideBaseAPIUrl: string;
  onResponse: (response: any) => void;
  onError?: (response: Response | undefined) => void;
}) => {
  const url = apiURLForPathWithOverrideUrl(overrideBaseAPIUrl, path);
  fetchWithoutAuthHelper({
    url,
    method,
    headers,
    body,
    onResponse,
    onError,
  });
};

export const fetchWithoutAuthHelper = async ({
  url,
  method,
  headers = {},
  body,
  onResponse,
  onError,
}: {
  url: string;
  method: string;
  headers: { [key: string]: string };
  body?: any;
  onResponse: (response: any) => void;
  onError?: (response: Response | undefined) => void;
}) => {
  const cookies = new Cookies();
  const hostname = new URL(url).hostname;
  const CSRFToken =
    (await cookies.get(`merge_csrftoken_${hostname}`)) || (await cookies.get(CSRF_COOKIE_NAME));

  const updatedHeaders: { [key: string]: string } = {
    ...headers,
    "Content-Type": "application/json;charset=UTF-8",
    "X-CSRFToken": CSRFToken,
  };

  try {
    const response = await fetch(url, {
      method,
      headers: updatedHeaders,
      body: body ? JSON.stringify(body) : null,
      credentials: "include",
    });

    if (!response.ok) {
      throw response;
    }

    const responseJson = await response.json();
    onResponse(responseJson);
  } catch (error: any) {
    if (onError) {
      onError(error && "json" in error ? error : undefined);
    }
  }
};

export const fetchWithAuth = async <DataType extends any = any>({
  path,
  method = HTTPMethod.GET,
  headers = {},
  body,
  onResponse,
  onError,
}: {
  path: string;
  method: string;
  headers?: { [key: string]: string };
  body?: any;
  onResponse: (response: DataType) => void;
  onError?: (response: Response | undefined) => void;
}) => {
  let updatedHeaders: { [key: string]: string } | Headers = headers || {};
  let updatedBody: any = body && method !== HTTPMethod.GET ? JSON.stringify(body) : null;
  const cookies = new Cookies();
  const authToken =
    (await cookies.get(AUTH_COOKIE_NAME)) || (await cookies.get("authentication_token"));
  const CSRFToken = await cookies.get(CSRF_COOKIE_NAME);

  let hasFile = false;
  for (const key in body) {
    if (body[key] instanceof Blob || body[key] instanceof File) {
      hasFile = true;
    }
  }

  if (hasFile) {
    updatedHeaders = new Headers();
    for (const key in headers) {
      updatedHeaders.append(key, headers[key]);
    }

    updatedBody = null;
    if (body) {
      updatedBody = new FormData();
      for (const key in body) {
        if (body[key] instanceof File) {
          updatedBody.append(key, body[key], body[key].name);
        } else {
          updatedBody.append(key, body[key]);
        }
      }
    }
    if (authToken) {
      updatedHeaders.append("Authorization", `Token ${authToken}`);
    }
    if (CSRFToken) {
      updatedHeaders.append("X-CSRFToken", CSRFToken);
    }
  } else {
    if (authToken) {
      updatedHeaders.Authorization = `Token ${authToken}`;
    }
    if (CSRFToken) {
      updatedHeaders["X-CSRFToken"] = CSRFToken;
    }
    updatedHeaders["Content-Type"] = "application/json;charset=UTF-8";
  }

  const url = apiURLForPath(path);

  try {
    const response = await fetch(url, {
      method,
      headers: updatedHeaders,
      body: updatedBody,
      credentials: "include",
    });

    if (response.status === 403) {
      // Read a clone: this Response is thrown below, and WebhookUrlInput.tsx
      // calls .json() on it to read the field errors.
      const data = await response
        .clone()
        .json()
        .catch(() => undefined);
      const isJSONBody = data !== undefined;

      if (["Invalid token.", "Token has expired."].includes(data?.detail)) {
        removeAuthTokenAndUserType();
        window.location.href = LOGIN_PATH;
      }

      // The catch below only forwards a thrown value that has `json`, and five
      // call sites read `err.status` to show the permissions message, so the
      // non-JSON case throws a stand-in that carries both.
      throw isJSONBody ? response : { status: response.status, json: async () => ({}) };
    }

    if (!response.ok) {
      throw response;
    }

    const data = response.status === 204 ? {} : await response.json();
    onResponse(data);
  } catch (error: any) {
    if (onError) {
      onError(error && "json" in error ? error : undefined);
    }
  }
};

/**
 * Same as above fetchWithAuth, but just returns a promise with the response from fetch and does NOT handle formData logic.
 */
export const fetchWithAuthAsync = async <T extends unknown>({
  path,
  method = HTTPMethod.GET,
  headers = {},
  body,
  signal,
}: {
  path: string;
  method?: string;
  headers?: { [key: string]: string };
  body?: any;
  signal?: RequestInit["signal"];
}): Promise<
  Omit<Response, "body"> & {
    body: T;
  }
> => {
  let updatedHeaders: { [key: string]: string } | Headers = headers || {};
  let updatedBody: any = body && method !== HTTPMethod.GET ? JSON.stringify(body) : null;

  const cookies = new Cookies();
  const authToken =
    (await cookies.get(AUTH_COOKIE_NAME)) || (await cookies.get("authentication_token"));
  const CSRFToken = await cookies.get(CSRF_COOKIE_NAME);
  let hasFile = false;
  for (const key in body) {
    if (body[key] instanceof Blob || body[key] instanceof File) {
      hasFile = true;
    }
  }
  if (hasFile) {
    updatedHeaders = new Headers();
    for (const key in headers) {
      updatedHeaders.append(key, headers[key]);
    }

    updatedBody = null;
    if (body) {
      updatedBody = new FormData();
      for (const key in body) {
        if (body[key] instanceof File) {
          updatedBody.append(key, body[key], body[key].name);
        } else {
          updatedBody.append(key, body[key]);
        }
      }
    }
    if (authToken) {
      updatedHeaders.append("Authorization", `Token ${authToken}`);
    }
    if (CSRFToken) {
      updatedHeaders.append("X-CSRFToken", CSRFToken);
    }
  } else {
    if (authToken) {
      updatedHeaders.Authorization = `Token ${authToken}`;
    }
    if (CSRFToken) {
      updatedHeaders["X-CSRFToken"] = CSRFToken;
    }
    updatedHeaders["Content-Type"] = "application/json;charset=UTF-8";
  }

  const url = apiURLForPath(path);

  const response = await fetch(url, {
    method,
    headers: updatedHeaders,
    body: updatedBody,
    signal,
  });

  if (!response.ok) {
    throw Object.assign(new Error(response.statusText), { status: response.status });
  }

  return {
    ...response,
    body: await response.json(),
  };
};

/**
 * Used in API tester. Makes fetch request using API Key + LinkedAccount account_token.
 */
export const fetchWithAPIKey = async ({
  path,
  method,
  headers = {},
  body,
  onResponse,
  onError,
  apiKey,
  accountToken,
}: {
  path: string;
  method: string;
  headers?: { [key: string]: string };
  body?: any;
  onResponse: (response: Response) => void;
  onError?: (response: Response | undefined) => void;
  apiKey: string;
  accountToken: string;
}) => {
  const cookies = new Cookies();
  const updatedHeaders: { [key: string]: string } | Headers = headers || {};

  const CSRFToken = await cookies.get(CSRF_COOKIE_NAME);

  if (CSRFToken) {
    updatedHeaders["X-CSRFToken"] = CSRFToken;
  }

  updatedHeaders["X-ACCOUNT-TOKEN"] = accountToken;
  updatedHeaders.Authorization = `Bearer ${apiKey}`;

  const url = apiURLForPath(path);

  try {
    const response = await fetch(url, {
      method,
      body: method === HTTPMethod.GET ? null : body,
      headers: updatedHeaders,
    });

    onResponse(response);
  } catch (error: any) {
    if (onError) {
      onError(error && "json" in error ? error : undefined);
    }
  }
};

export const fetchCurrentUser = (setUser: (response: any) => void) => {
  fetchWithAuth({
    path: "/users/me",
    method: HTTPMethod.GET,
    onResponse: (data: UserWithBillingPlan) => {
      const cookies = new Cookies();
      cookies.set(USER_TYPE_COOKIE_NAME, data.type, {
        maxAge: SEC_IN_YEAR,
        secure: true,
      });
      setUser(data);
    },
  });
};

export const fetchStaticLinkedAccountCount = (onResponse: (count: number | undefined) => void) => {
  fetchWithAuth({
    path: "/billing/plans/static-linked-account-count",
    method: HTTPMethod.GET,
    onResponse: (data: { static_production_linked_account_count: number }) => {
      onResponse(data.static_production_linked_account_count);
    },
  });
};

export const fetchLinkedAccountFieldMappingInstances = (
  linkedAccountID: string,
  setFieldMappingInstances: (response: any) => void,
  onSuccess: () => void,
) => {
  fetchWithAuth({
    path: `integrations/linked-account/field-mappings/${linkedAccountID}/field-mapping-instances`,
    method: HTTPMethod.GET,
    onResponse: (data) => {
      setFieldMappingInstances(data);
      onSuccess();
    },
  });
};

export const fetchFieldMappingInstance = (
  fieldMappingInstanceID: string,
  setFieldMappingInstance: (response: any) => void,
  callBack?: () => void,
) => {
  fetchWithAuth({
    path: `integrations/linked-account/field-mapping-instance/${fieldMappingInstanceID}`,
    method: HTTPMethod.GET,
    onResponse: (data: any) => {
      setFieldMappingInstance(data);
      if (callBack) {
        callBack();
      }
    },
  });
};

export const editFieldMappingInstance = (
  fieldMappingInstanceID: string,
  editedFieldMappingInstance: EditFieldMappingInstanceProps,
  onResponse: () => void,
) => {
  fetchWithAuth({
    path: `integrations/linked-account/field-mapping-instance/${fieldMappingInstanceID}`,
    body: editedFieldMappingInstance,
    method: HTTPMethod.PATCH,
    onResponse,
  });
};

export const fetchIntegrationWideFieldMappingOptions = (
  integrationId: string,
  fieldMappingTargetId: string,
  commonModel: string,
  setFieldMappingOptions: (response: any) => void,
) => {
  fetchWithAuth({
    path: `integrations/field-mappings/${integrationId}/${fieldMappingTargetId}/meta`,
    method: HTTPMethod.GET,
    onResponse: (data: any) => {
      setFieldMappingOptions(createFieldMappingOptions(data.available_field_mappings, commonModel));
    },
    onError: () => {
      showErrorToast("Unable to fetch Field Mapping options");
    },
  });
};

export const fetchIntegrationWideOverrideOptions = (
  integrationId: string,
  overrideTargetID: string,
  commonModel: string,
  setFieldMappingOptions: (response: any) => void,
) => {
  fetchWithAuth({
    path: `integrations/field-mappings/${integrationId}/${overrideTargetID}/meta?is_common_model_override=True`,
    method: HTTPMethod.GET,
    onResponse: (data: any) => {
      setFieldMappingOptions(data.available_field_mappings);
    },
    onError: () => {
      showErrorToast("Unable to fetch Field Mapping options");
    },
  });
};

export const deleteLinkedAccountFieldMapping = (
  fieldMappingID: string,
  refreshFieldMappings?: () => void,
  onSuccess?: (data: any) => void,
  skipFieldMappingRefresh?: boolean,
) => {
  fetchWithAuth({
    path: `integrations/linked-account/field-mappings/${fieldMappingID}/delete`,
    method: HTTPMethod.DELETE,
    onResponse: (data: any) => {
      if (refreshFieldMappings && !skipFieldMappingRefresh) {
        refreshFieldMappings();
      }
      if (onSuccess) {
        onSuccess(data);
      }
    },
  });
};

export type NewFieldMappingProps = {
  linked_account_id: string;
  common_model_id: string;
  field_key: string;
  origin_type: string;
  field_traversal_path: Array<string>;
  field_description?: string;
  configured_by: FieldMappingSource;
  create_for_organization: boolean;
  field_mapping_target_id?: string;
  api_endpoint_id: string;
  display_name: string;
  jmes_path?: string | null;
  advanced_mapping_expression?: string | null;
};

export type NewIntegrationWideFieldMapping = {
  integration_id: string;
  organization_id: string;
  common_model_id: string;
  field_key: string;
  origin_type: string;
  field_traversal_path: Array<string>;
  field_description?: string;
  configured_by: FieldMappingSource;
  create_for_organization: boolean;
  field_mapping_target_id?: string;
  api_endpoint_id: string;
  display_name: string;
  enable_linked_account_level_overrides?: boolean;
};

export type EditFieldMappingInstanceProps = {
  field_mapping_instance_id: string;
  field_traversal_path: Array<string>;
  field_description?: string;
  api_endpoint_id: string;
  display_name: string;
  enable_linked_account_level_overrides?: boolean;
  origin_type: string;
  jmes_path?: string | null;
  advanced_mapping_expression?: string | null;
};
export const createLinkedAccountFieldMapping = (
  body: NewFieldMappingProps,
  onResponse: (data?: any) => void,
  onError?: (response: Response | undefined) => void,
) => {
  fetchWithAuth({
    path: `integrations/linked-account/field-mappings/create`,
    method: HTTPMethod.POST,
    body,
    onResponse: (data: any) => {
      onResponse(data.field_mapping_instance_id);
    },
    onError,
  });
};

export const createIntegrationWideFieldMapping = (
  body: NewIntegrationWideFieldMapping,
  isLoading: () => void,
  handleFieldMappingInstanceID?: (id: string) => void,
) => {
  fetchWithAuth({
    path: `integrations/linked-account/field-mappings/create`,
    method: HTTPMethod.POST,
    body,
    onResponse: (data: any) => {
      isLoading();
      handleFieldMappingInstanceID!(data?.field_mapping_instance_id);
    },
    onError: () => showErrorToast("Unable to create Field Mapping Instance"),
  });
};

const isNewAuthTokenMethod = (): boolean => {
  const cookies = new Cookies();
  return !!cookies.get(AUTH_INDICATOR_NAME) || !!cookies.get(AUTH_TOKEN_INDICATOR);
};

export const getAuthToken = (): string | null => {
  const cookies = new Cookies();
  // currently we pass this cookie in a way where the frontend and backend can both set and get it - however
  // in the new work we will pass the cookie from the backend in a way where the frontend cant get it (httponly)
  // for this reason passing a second cookie that the frontend can read that only contains an indicator of if the
  // auth token was passed or not
  const cookieName = isNewAuthTokenMethod() ? AUTH_INDICATOR_NAME : AUTH_COOKIE_NAME;
  const authToken: string | null = cookies.get(cookieName);
  if (!authToken) {
    // fallback to unsuffixed cookie during migration
    const fallbackName = isNewAuthTokenMethod() ? AUTH_TOKEN_INDICATOR : "authentication_token";
    return cookies.get(fallbackName) || null;
  }
  return authToken;
};

export const hasAuthToken = () => {
  return getAuthToken() !== null;
};

export const getCookieDomainForEnv = () =>
  (process.env.REACT_APP_MERGE_ENV == "PRODUCTION" ||
    process.env.REACT_APP_MERGE_ENV == "DEVELOP" ||
    process.env.REACT_APP_MERGE_ENV == "MU-DEVELOP" ||
    process.env.REACT_APP_MERGE_ENV == "MU-STAGING") &&
  !process.env.REACT_APP_MERGE_PROD_FROM_LOCAL
    ? process.env.REACT_APP_BASE_API_URL != null &&
      process.env.REACT_APP_BASE_API_URL.includes("openaimerge")
      ? ".openaimerge.com"
      : process.env.REACT_APP_BASE_API_URL != null &&
        process.env.REACT_APP_BASE_API_URL.includes("dropboxmerge")
      ? ".dropboxmerge.com"
      : ".merge.dev"
    : undefined;

export const setAuthTokenAndUserType = (authToken: string, userType: string) => {
  const cookieDomain = getCookieDomainForEnv();
  const cookies = new Cookies();
  // TODO (Ankit): Remove this after testing - we shouldnt need to manually set this cookie anymore
  if (!isNewAuthTokenMethod()) {
    cookies.set(AUTH_COOKIE_NAME, authToken, {
      maxAge: SEC_IN_DAY,
      domain: cookieDomain,
      path: "/",
      secure: true,
      sameSite: "lax",
    });
  }
  cookies.set(USER_TYPE_COOKIE_NAME, userType, {
    maxAge: SEC_IN_YEAR,
    secure: true,
    path: "/",
    domain: cookieDomain,
  });
  clearSwitchedFrom("unified");
};

export const setUserType = (userType: string) => {
  const cookies = new Cookies();
  const cookieDomain = getCookieDomainForEnv();

  cookies.set(USER_TYPE_COOKIE_NAME, userType, {
    maxAge: SEC_IN_YEAR,
    secure: true,
    path: "/",
    domain: cookieDomain,
  });
};

export const removeAuthTokenAndUserType = () => {
  const cookies = new Cookies();
  const cookieDomain = getCookieDomainForEnv();

  if (isNewAuthTokenMethod()) {
    cookies.remove(AUTH_INDICATOR_NAME, { path: "/", domain: cookieDomain });
    if (AUTH_INDICATOR_NAME !== AUTH_TOKEN_INDICATOR) {
      cookies.remove(AUTH_TOKEN_INDICATOR, { path: "/", domain: cookieDomain });
    }
  }

  cookies.remove(AUTH_COOKIE_NAME, { path: "/", domain: cookieDomain });
  if (AUTH_COOKIE_NAME !== "authentication_token") {
    cookies.remove("authentication_token", { path: "/", domain: cookieDomain });
  }
  cookies.remove(USER_TYPE_COOKIE_NAME, { path: "/", domain: cookieDomain });
  if (USER_TYPE_COOKIE_NAME !== "user_type") {
    cookies.remove("user_type", { path: "/", domain: cookieDomain });
  }
  cookies.remove(CSRF_COOKIE_NAME, { path: "/", domain: cookieDomain });
  clearSwitchedFrom("unified");
};

export const onLogout = ({ onError }: { onError: () => void }) => {
  // Clear PostHog identity before the request fires so the explicit-logout
  // event and the auth-side reset stay aligned even if the redirect happens
  // before the response handler runs. posthog.reset() also clears all super
  // properties, so clearPostHogIdentity re-registers product: "unified"
  // immediately to keep it on any events captured before the page reloads.
  clearPostHogIdentity();
  return fetchWithAuth({
    path: "/users/logout",
    method: HTTPMethod.POST,
    onResponse: () => {
      removeAuthTokenAndUserType();
      window.location.href = LOGIN_PATH;
    },
    onError,
  });
};

const getCommonModelFieldsMap = (fields: CommonModelField[]) => {
  return fields.reduce((fieldsMap: CommonModelFieldMap, field: CommonModelField) => {
    return {
      ...fieldsMap,
      [field.field_name]: {
        isEnabled: field.is_enabled,
        fieldDescription: field.field_description,
        fieldType: field.field_type,
      },
    };
  }, {});
};

const getLinkedAccountActionOverrides = (commonModelToggleScopeInfo: any) => {
  return commonModelToggleScopeInfo.linked_account_scoped_actions || [];
};
const getLinkedAccountFieldOverrides = (commonModelToggleScopeInfo: any) => {
  return commonModelToggleScopeInfo.linked_account_scoped_fields || [];
};

export const createScopeMap = (commonModelToggles: CommonModelToggle[]) => {
  const commonModelTogglesMap = commonModelToggles.reduce(
    (commonModelTogglesMap: CateogryScopeMap, commonModelToggle: CommonModelToggle) => {
      const commonModelFieldsMap = getCommonModelFieldsMap(commonModelToggle.fields);
      const linkedAccountActionOverrides = getLinkedAccountActionOverrides(
        commonModelToggle.scope_level_info,
      );
      const linkedAccountFieldOverrides = getLinkedAccountFieldOverrides(
        commonModelToggle.scope_level_info,
      );

      return {
        ...commonModelTogglesMap,
        [commonModelToggle.name]: {
          actions: commonModelToggle.enabled_model_actions,
          capabilities: commonModelToggle.model_capabilities,
          model_description: commonModelToggle.model_description,
          optional_actions: commonModelToggle.end_user_configurable_actions,
          fields: commonModelFieldsMap,
          linkedAccountActionOverrides: linkedAccountActionOverrides,
          linkedAccountFieldOverrides: linkedAccountFieldOverrides,
        },
      };
    },
    {},
  );
  return commonModelTogglesMap;
};

export const getLinkedAccountCommonModelTogglesMap = (
  linkedAccount: LinkedAccount,
  setLinkedAccountLevelCommonModelScopes: (response: any) => void,
) => {
  fetchWithAuth({
    path: `integrations/common-model-toggles/${linkedAccount.category}?linked_account_id=${linkedAccount.id}`,
    method: HTTPMethod.GET,
    onResponse: (commonModelToggles) => {
      setLinkedAccountLevelCommonModelScopes(createScopeMap(commonModelToggles));
    },
  });
};

export const getOrgCommonModelTogglesMap = (
  category: string,
  setOrgLevelCommonModelScopes: (response: any) => void,
) => {
  fetchWithAuth({
    path: `integrations/common-model-toggles/${category}`,
    method: HTTPMethod.GET,
    onResponse: (commonModelToggles) => {
      setOrgLevelCommonModelScopes(createScopeMap(commonModelToggles));
    },
  });
};

export const toggleRedactUnmappedData = (
  enabled: boolean,
  organizationId?: string,
  linkedAccountId?: string,
  onResponse?: (response: {
    feature_enabled_for_organization?: boolean;
    feature_enabled_for_linked_account?: boolean;
  }) => void,
  onError?: (response: Response | undefined) => void,
) => {
  fetchWithAuth({
    path: `integrations/redact-unmapped-data/toggle`,
    method: HTTPMethod.POST,
    body: {
      organization_id: organizationId,
      linked_account_id: linkedAccountId,
      enabled,
    },
    onResponse: (res: {
      feature_enabled_for_organization?: boolean;
      feature_enabled_for_linked_account?: boolean;
    }) => {
      if (onResponse) onResponse(res);
    },
    onError: (err) => {
      if (onError) onError(err);
    },
  });
};

export const postUpgradeInterest = (
  user: User,
  requestedPlanUpgrade: boolean,
  setRequestedPlanUpgrade: (response: any) => void,
) => {
  if (!requestedPlanUpgrade) {
    fetchWithAuth({
      path: `/users/request-upgrade`,
      method: HTTPMethod.PATCH,
      body: { user_email: user.email, time_request: Date.now() },
      onResponse: () => {
        showInfoToast(
          "Successfully requested plan upgrade! The Merge team will be in touch shortly!",
        );
        setRequestedPlanUpgrade(true);
      },
      onError: () => {
        showErrorToast("Failed to indicate interest in upgrade. Please try again.");
      },
    });
  }
};

export type CustomizableLinkPostBody = {
  status: string;
  organization_customization_data: OrganizationCustomization | null;
  integration_customizations_data: IntegrationCustomization[] | null;
  logo: File | string | null;
};

export const publishCustomizableLinkChanges = (body: CustomizableLinkPostBody) => {
  // build a *plain* object, not a FormData
  const payload: Record<string, any> = {
    status: body.status,
    organization_customization_data: body.organization_customization_data
      ? JSON.stringify(body.organization_customization_data)
      : "{}", // serializer will load this into {}
    integration_customizations_data: body.integration_customizations_data
      ? JSON.stringify(body.integration_customizations_data)
      : "null", // serializer will load this into None/[]
    logo: body.logo instanceof File ? body.logo : undefined, // either a File or undefined so that it can properly send formData or plain object
  };

  return fetchWithAuth({
    path: `/integrations/create-update-customizable-link-settings`,
    method: HTTPMethod.POST,
    body: payload, // <-- just pass the object
    onResponse: () => showSuccessToast("Successfully saved changes to Merge Link!"),
    onError: () => showErrorToast("Failed to save changes. Please try again."),
  });
};

export const fetchCreateTestLinkToken = (
  setLinkToken: (linkToken: string) => void,
  setAccountLimitReached?: (limitReached: boolean) => void,
) => {
  fetchWithAuth({
    path: "/integrations/create-test-link-token",
    method: HTTPMethod.POST,
    onResponse: (data) => {
      setLinkToken(data.link_token);
    },
    onError: (err: Response | undefined) => {
      if (err && err.status === 403) {
        if (setAccountLimitReached) {
          setAccountLimitReached(true);
          setLinkToken("");
        }
        // Optionally, you can show a toast message here if needed
      } else {
        showErrorToast(
          "Unable to initialize new test Linked Accounts, please check your connection and try again.",
        );
      }
    },
  });
};

export const publishWhiteLabelGuideChanges = (
  whiteLabelGuide: WhiteLabelGuideType,
  handleHasSavedWhiteLabelGuide: (handleHasSavedWhiteLabelGuide: boolean) => void,
): Promise<boolean> => {
  return new Promise((resolve) => {
    fetchWithAuth({
      path: "/organizations/white-label-guide-configuration",
      method: HTTPMethod.POST,
      body: whiteLabelGuide,
      onResponse: () => {
        showSuccessToast("Successfully saved white-label guides!");
        handleHasSavedWhiteLabelGuide(true);
        resolve(true);
      },
      onError: () => {
        showErrorToast("Failed to save white-label guides.");
        resolve(false);
      },
    });
  });
};

export const getOrganizationMappingInfo = (
  category: string,
  onSuccess: (data: any) => void,
  onFailure: () => void,
) => {
  fetchWithAuth({
    path: `integrations/organization-mapping-info/${category}`,
    method: HTTPMethod.GET,
    onResponse: (data) => {
      onSuccess(data);
    },
    onError: (err) => {
      onFailure();
    },
  });
};

export const getGeneratedAIFieldMapping = (
  body: {
    fm_generation_prompt: string;
    organization_id: string;
    provided_remote_data_json: string;
    logging_metadata: {
      linked_account_id?: string;
      remote_field?: string;
    };
  },
  onSuccess: (data: any) => void,
  onFailure: (errorMessage?: string) => void,
) => {
  fetchWithAuth({
    path: `integrations/linked-account/field-mappings/create-ai-generated-adv-field-mapping`,
    method: HTTPMethod.POST,
    body,
    onResponse: (data) => {
      onSuccess(data);
    },
    onError: async (err) => {
      if (err) {
        try {
          const data = await err.json();
          const raw = data?.non_field_errors?.[0] || data?.detail;
          if (raw) {
            const message = raw.replace(/^Failed to generate field mapping:\s*/i, "");
            onFailure(message);
            return;
          }
        } catch {
          // Response body unavailable, fall through
        }
      }
      onFailure();
    },
  });
};
